<template>
  <b-container class="edit-promo-container">
    <b-form>
      <b-form-group label="Name der Kampagne:" :disabled="!canEdit">
        <b-form-input
          placeholder="z.B. News"
          v-model="promotion.name"
          :state="promotion.name && promotion.name.trim().length > 0"
        >
        </b-form-input>
      </b-form-group>
      <b-form-group label="Beschreibung:" :disabled="!canEdit">
        <b-form-textarea
          placeholder="z.B. Aktuelle Informationen"
          rows="3"
          v-model="promotion.description"
        >
        </b-form-textarea>
      </b-form-group>
      <b-form-group
        label="Kampagnentypen:"
        description="Wählen Sie die Plattformen aus, auf denen die Kampagne angezeigt werden soll"
        :disabled="!canEdit"
      >
        <div class="checkbox-container">
          <b-form-checkbox
            v-for="option of availableCampaignTypes"
            :key="option.key"
            @change="campaignTypeChecked($event, option.key)"
            :disabled="
              option.key ===
              'player' /* disable, as this is the only working option right now */
            "
            :checked="
              promotion.campaignTypeKeys &&
              promotion.campaignTypeKeys.includes(option.key)
            "
          >
            {{ option.valueGER }}
          </b-form-checkbox>
        </div>
      </b-form-group>
    </b-form>
    <div v-if="!canEdit" class="no-permission-alert">
      Sie haben keine Berechtigung um diese Kampagne zu bearbeiten
    </div>

    <hr />

    <template v-if="canEdit">
      <b-form-group label="Verfügbar für:">
        <b-form-group label="Firmengruppen">
          <div class="checkbox-container">
            <b-form-checkbox
              v-for="group of companyGroups"
              :key="group.id"
              @change="companyGroupChecked($event, group.id)"
              :checked="promotion.companyGroupIds.includes(group.id)"
            >
              <div v-if="group.noPerm" style="color: var(--col-error)">
                <del title="Keine Berechtigung für diese Gruppe">
                  {{ group.name }}
                </del>
                <i
                  v-if="promotion.companyGroupIds.includes(group.id)"
                  style="width: 1rem; margin-left: 0.5rem"
                  class="fa fa-exclamation-circle"
                ></i>
                <div v-else style="width: 1.5rem; display: inline-block"></div>
              </div>
              <span v-else>{{ group.name }}</span>
            </b-form-checkbox>
          </div>
        </b-form-group>
        <b-form-group label="Firmen">
          <div class="checkbox-container">
            <b-form-checkbox
              v-for="company of companies"
              :key="company.id"
              @change="companyChecked($event, company.id)"
              :checked="promotion.companyIds.includes(company.id)"
            >
              <div v-if="company.noPerm" style="color: var(--col-error)">
                <del title="Keine Berechtigung für diese Firma">
                  {{ company.name }}
                </del>
                <i
                  v-if="promotion.companyIds.includes(company.id)"
                  style="width: 1rem; margin-left: 0.5rem"
                  class="fa fa-exclamation-circle"
                ></i>
                <div v-else style="width: 1.5rem; display: inline-block"></div>
              </div>
              <span v-else>{{ company.name }}</span>
            </b-form-checkbox>
          </div>
        </b-form-group>
        <template slot="description">
          Wählen Sie die Firmen bzw. FirmenGruppen aus, für die die Kampagne
          verfügbar sein soll.
          <br />
          Ist ein Name durchgestrichen, so haben Sie keine Berechtigung, um für
          diese Firma (oder Gruppe) Promotions anzulegen.
          <br />
          Um die Kampagne dennoch bearbeiten zu können, müssen Sie diese zuerst
          abwählen. Dadurch ist die Kampagne jedoch nicht mehr für die Firma
          bzw. Gruppe verfügbar.
        </template>
      </b-form-group>

      <b-alert :show="displayAccessWarning" variant="warning">
        Sind sowohl Firmen als auch Firmengruppen ausgewählt, kann es sein, dass
        User der Firmen (oder -Gruppen) ungewollt Zugriff auf die Kampagne
        bekommen und diese verändern bzw. Dateien hochladen können!<br />
        Es empfiehlt sich daher, entweder Firmen oder Firmengruppen auszuwählen.
      </b-alert>
      <b-alert :show="promotion.companyGroupIds.length > 0" variant="info">
        Hinweis: Ist eine Kampagne für eine Firmengruppe verfügbar, ist diese
        für manche User der Firmen ersichtlich. Diese Benutzer können die
        Kampagne weder verändern, noch Dateien hochladen, jedoch bereits
        hochgeladene Dateien einsehen.
      </b-alert>
    </template>

    <hr />

    <b-row>
      <b-col>
        <buttonc
          label="Speichern"
          class="green"
          @click="savePromo"
          :disabled="!canEdit || !canSubmit"
        />
      </b-col>
      <b-col
        v-if="promotion.id && !promotion.deleted_at"
        style="text-align: right"
      >
        <buttonc
          icon="fa-trash"
          class="red"
          @click="deletePromo"
          :disabled="!canDelete"
        />
      </b-col>
    </b-row>

    <b-row v-if="promotion.deleted_at" class="recover-section">
      <b-col>
        <i>
          Diese Kampagne wurde am
          {{ promotion.deleted_at | formatDate }} gelöscht...
        </i>
        Wiederherstellen?
      </b-col>
      <buttonc icon="fa-undo" @click="recoverPromo" />
    </b-row>
  </b-container>
</template>

<script>
import moment from "moment";
import buttonc from "@/components/uicomponents/button";
import { mapGetters } from "vuex";

/**
 * @typedef {Object} Promotion
 * @property {?number} id
 * @property {?string} name
 * @property {?string} description
 * @property {?string} deleted_at
 * @property {['player'|'app']} campaignTypeKeys
 * @property {Array} companyGroupIds
 * @property {Array} companyIds
 * @property {Array} companyGroupNames
 * @property {Array} companyNames
 */

export default {
  name: "EditPromotion",
  components: { buttonc },
  data() {
    /** @type Promotion */
    let promotion = {
      // Default Promotion state
      companyGroupIds: [...this.selectedCompanyGroups],
      companyIds: [...this.selectedCompanies],
      campaignTypeKeys: [
        "player" /* TODO: remove player when more options are available */,
      ],
    };
    promotion = Object.assign(promotion, this.inputPromotion || {});
    return {
      /** @type Promotion */
      promotion,
    };
  },
  props: {
    inputPromotion: {
      /** @type {import('vue').PropType<Promotion>} */
      type: Object,
      default: null,
    },
    selectedCompanies: {
      type: Array,
      default: () => [],
    },
    selectedCompanyGroups: {
      type: Array,
      default: () => [],
    },
    canEdit: {
      type: Boolean,
      default: () => true,
    },
    canDelete: {
      type: Boolean,
      default: () => true,
    },
  },
  created() {
    this.loadCampaignTypes();
  },
  computed: {
    ...mapGetters(["availableCampaignTypes"]),
    companyGroups() {
      const companyGroups = this.$store.state.companyGroups;
      const mappedGroupNames = this.promotion?.companyGroupNames
        ? Object.entries(
            this.promotion.companyGroupNames
          ).map(([id, name]) => ({ id: parseInt(id), name, noPerm: true }))
        : [];
      const combinedCompanyGroups = [...mappedGroupNames, ...companyGroups];
      return Object.values(
        Object.fromEntries(combinedCompanyGroups.map((grp) => [grp.id, grp]))
      );
    },
    companies() {
      const companies = this.$store.state.companies;
      const mappedCompanyNames = this.promotion?.companyNames
        ? Object.entries(this.promotion.companyNames).map(([id, name]) => ({
            id: parseInt(id),
            name,
            noPerm: true,
          }))
        : [];
      const combinedCompanies = [...mappedCompanyNames, ...companies];
      return Object.values(
        Object.fromEntries(combinedCompanies.map((comp) => [comp.id, comp]))
      );
    },
    authorizationHeader() {
      return { Authorization: "Bearer " + this.$store.state.jwt };
    },
    displayAccessWarning() {
      return (
        this.promotion.companyIds.length !== 0 &&
        this.promotion.companyGroupIds.length !== 0
      );
    },
    canSubmit() {
      if (!this.promotion?.name) return false;

      const hasPermissionForGroups =
        this.companyGroups.filter(
          (grp) => grp.noPerm && this.promotion.companyGroupIds.includes(grp.id)
        ).length === 0;
      const hasPermissionForCompanies =
        this.companies.filter(
          (comp) => comp.noPerm && this.promotion.companyIds.includes(comp.id)
        ).length === 0;
      const hasPerms = hasPermissionForCompanies && hasPermissionForGroups;
      return this.promotion.name.trim().length > 0 && hasPerms;
    },
  },
  methods: {
    async loadCampaignTypes() {
      // TODO: debounce
      const response = await this.axios.get("/api/sec/list/campaignTypes", {
        headers: this.authorizationHeader,
      });
      this.$store.commit("setAvailableCampaignTypes", response.data);
    },

    campaignTypeChecked(state, key) {
      const newKeys = [...(this.promotion.campaignTypeKeys || []), key].filter(
        (k) => k !== key || state
      );
      this.promotion = { ...this.promotion, campaignTypeKeys: newKeys };
    },
    companyGroupChecked(state, id) {
      const newGroups = [...(this.promotion.companyGroupIds || []), id].filter(
        (k) => k !== id || state
      );
      this.promotion = { ...this.promotion, companyGroupIds: newGroups };
    },
    companyChecked(state, id) {
      const newCompanies = [...(this.promotion.companyIds || []), id].filter(
        (k) => k !== id || state
      );
      this.promotion = { ...this.promotion, companyIds: newCompanies };
    },

    savePromo() {
      this.$emit("changed", this.promotion);
    },
    deletePromo() {
      this.$emit("deleted", this.promotion);
    },
    recoverPromo() {
      // TODO: Implement Promotion recover logic
    },
  },
  filters: {
    formatDate: (value) =>
      value ? moment(String(value)).format("DD.MM.YYYY HH:mm") : null,
  },
};
</script>

<style scoped>
.edit-promo-container {
  text-align: left;
  color: var(--contrast-4);
  background-color: var(--contrast-1);
  padding: 0.5rem;
}

.edit-promo-container .button {
  padding: 1rem;
}

.edit-promo-container .button .label {
  color: white !important;
}

.checkbox-container {
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  flex-wrap: wrap;
  /* Display max ~10 lines and cut the last one, so it's visible to users that there are more companies */
  max-height: 14.5rem;
  overflow-y: scroll;
}

.checkbox-container > .custom-checkbox {
  margin-right: 1rem;
}

.recover-section {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
}

.form-control {
  background-color: var(--contrast-2);
  border: 2px solid var(--contrast-3);
  box-shadow: none;
}

.form-control::placeholder {
  color: var(--contrast-5);
}
</style>
